/* eslint-disable react-hooks/exhaustive-deps */
import { useMachine } from '@xstate/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { InterpreterFrom, assign } from 'xstate';

import { useCmsContent } from '@src/contexts/cms-content-context';
import { useToast } from '@src/contexts/toast-context';
import { useAnalytics } from '@src/features/analytics/hooks';
import { AnalyticsEvent } from '@src/features/analytics/types';
import { useAuth } from '@src/features/auth/contexts/auth-context';
import { authMachine } from '@src/features/auth/state-machine/auth-machine';
import { AuthEntryStep } from '@src/features/auth/state-machine/types';
import { cacheProfile } from '@src/features/profile/lib/profile-cache';
import { profileService } from '@src/features/profile/services';
import { useLocale } from '@src/hooks/use-locale';
import { Brand } from '@src/types/content/brand';

import { SocialProvider } from '../services/i-auth-service';

function sanitizeAndCheckUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.hostname.endsWith('numastays.com') || parsedUrl.hostname.endsWith('numa.vercel.app')) {
      return parsedUrl.href;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves auth-relevant query params from router
 */
const useAuthQuery = () => {
  const { query } = useRouter();
  const step = (query.step as AuthEntryStep[] | undefined)?.[0] ?? 'overview';
  const actionCode = query.actionCode as string | undefined;
  const oAuthCode = query.code as string | undefined;
  const email = query.email as string | undefined;
  const provider = query.provider as SocialProvider | undefined;
  const fullName = query['full-name'] as string | undefined;

  return {
    step,
    actionCode,
    oAuthCode,
    email,
    provider,
    fullName,
  };
};

/**
 * Syncs the state machine with the router's state:
 * 1. Handle social oauth redirects with a "code" query parameter.
 * 2. Trigger custom actions via url, such as forgot password verification. An action requires a verification code to be present in the url.
 * 3. Syncs the machine's state to the url
 */
const useSyncMachineWithRouter = (service: InterpreterFrom<typeof authMachine>, isReady: boolean) => {
  const { step, actionCode, oAuthCode, email, fullName, provider } = useAuthQuery();

  useEffect(() => {
    if (!oAuthCode) {
      return;
    }

    service.send({
      type: 'OAUTH_REDIRECT',
    });
  }, [oAuthCode]);

  useEffect(() => {
    if (service.getSnapshot().matches(step)) {
      return;
    }

    service.send({
      type: 'ROUTE_CHANGE',
      step,
    });
  }, [step]);

  useEffect(() => {
    if (!actionCode || !email) {
      return;
    }

    if (!isReady) {
      return;
    }

    service.send({
      type: 'VERIFY',
      email,
      verificationCode: actionCode,
    });
  }, [actionCode, email, isReady]);

  useEffect(() => {
    if (!provider || !isReady) {
      return;
    }

    service.send({
      type: 'SOCIAL_LOGIN',
      provider,
    });
  }, [provider, isReady]);

  // please don't change order of PREFILL_SIGN_UP -> SUBMIT_EMAIL
  // It ensures that fullname is in context if sign-up is rendered
  useEffect(() => {
    if (!fullName) {
      return;
    }

    service.send({
      type: 'PREFILL_SIGN_UP',
      fullName,
    });
  }, [fullName]);

  useEffect(() => {
    if (!email || !isReady) {
      return;
    }
    service.send({
      type: 'SUBMIT_EMAIL',
      email,
    });
  }, [actionCode, email, isReady]);
};

/**
 * Syncs the url with the machine's current path context value.
 */
const useSyncRouterWithMachine = (service: InterpreterFrom<typeof authMachine>) => {
  const router = useRouter();
  const locale = useLocale();
  const { step } = useAuthQuery();

  const onPathChanged = useCallback(
    (path: string) => {
      if (path === step) {
        return;
      }
      router.push(`/auth/${path}`, undefined, {
        shallow: true,
        locale,
      });
    },
    [step],
  );

  useEffect(() => {
    const { unsubscribe } = service.subscribe(({ context }) => {
      onPathChanged(context.path);
    });

    return () => unsubscribe();
  }, []);
};

const useMachineDone = (service: InterpreterFrom<typeof authMachine>) => {
  const locale = useLocale();

  useEffect(() => {
    const onDone = async () => {
      const profile = await profileService.getProfile();
      cacheProfile(profile);

      if (profile?.isMandatoryInfoComplete === false) {
        window.location.href = new URL(`${locale}/profile/complete-profile`, window.location.origin).href;
        return;
      }

      const redirectToParam = service.getSnapshot().context.redirectTo;

      let redirectionPath = new URL(`/${locale}`, window.location.origin).href;

      if (redirectToParam) {
        // Decode the redirectToParams and validate if the root domain is numastays.com
        const redirectTo = decodeURIComponent(redirectToParam);
        const url = sanitizeAndCheckUrl(redirectTo);
        if (url) {
          redirectionPath = url;
        }
      }

      window.location.href = redirectionPath;
    };

    service.onDone(onDone);

    return () => {
      service.off(onDone);
    };
  }, []);
};

export const useAuthMachine = (brand: Brand) => {
  const { step } = useAuthQuery();
  const initialPath = useRef(step);
  const { query } = useRouter();
  const locale = useLocale();
  const { addToast } = useToast();
  const content = useCmsContent();
  const { trackEvent } = useAnalytics();

  const machine = useMachine(() => authMachine, {
    context: {
      path: initialPath.current,
      brandId: brand.id,
      language: locale,
      redirectTo: query.redirectTo as string | undefined,
      provider:
        typeof window === 'undefined'
          ? undefined
          : ((localStorage.getItem('provider') as SocialProvider | null) ?? undefined),
    },
    actions: {
      setRedirectUrl: assign(({ path }) => ({
        redirectUrl: new URL(`${locale}/auth/${path}`, window.location.origin),
      })),
      showVerificationResendSuccess: async () => {
        addToast({
          type: 'success',
          title: content.success,
          content: content.verificationCodeEmailSent,
        });
      },
      showConfirmationSuccess: async () => {
        addToast({
          type: 'success',
          title: content.success,
          content: '',
        });
      },
      trackUserSignedUpEvent: async ({ provider }) => {
        trackEvent({
          event: AnalyticsEvent.UserSignedUp,
          properties: {
            auth_provider: provider ?? 'email',
          },
        });
      },
      trackUserConfirmedEvent: async () => {
        trackEvent({
          event: AnalyticsEvent.UserConfirmed,
          properties: {
            auth_provider: 'email',
          },
        });
      },
      trackUserSignedInEvent: async ({ provider }) => {
        trackEvent({
          event: AnalyticsEvent.UserSignedIn,
          properties: {
            auth_provider: provider ?? 'email',
          },
        });
      },
    },
  });
  const [state, send, service] = machine;

  const { isLoggedIn, isReady } = useAuth();

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    if (!state.matches('overview')) {
      return;
    }

    const redirectTo = localStorage.getItem('redirectTo') ?? undefined;

    send({
      type: 'RESOLVE_SOCIAL_LOGIN',
      redirectTo,
    });
  }, [isLoggedIn]);

  useSyncMachineWithRouter(service, isReady);
  useSyncRouterWithMachine(service);
  useMachineDone(service);

  return {
    machine,
    isReady,
  };
};
